import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    Alert,
    useTheme
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';

const ReportProgress = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { batchId } = useParams();
    const navigate = useNavigate();
    const signOut = useSignOut();

    const [batchData, setBatchData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const intervalRef = useRef(null);

    const handleLogout = () => {
        signOut();
        localStorage.removeItem('userName');
        localStorage.removeItem('who');
        navigate('/signin');
    };

    const fetchBatchStatus = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/reports/batch/${batchId}/status`,
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            setBatchData(response.data);
            setLoading(false);

            // Stop polling when all reports are done
            if (response.data.pending + response.data.processing === 0) {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else if (error.response?.status === 404) {
                setError('Batch not found');
                setLoading(false);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            } else {
                console.error('Error fetching batch status:', error);
                // Continue polling even on error
            }
        }
    };

    useEffect(() => {
        localStorage.setItem('selectedMenu', 'Reports');

        // Initial fetch
        fetchBatchStatus();

        // Start polling every 5 seconds
        intervalRef.current = setInterval(fetchBatchStatus, 5000);

        // Cleanup on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [batchId]);

    const handleRetry = async (reportId) => {
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/reports/${reportId}/retry`,
                {},
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );
            // Restart polling if it was stopped
            if (!intervalRef.current) {
                intervalRef.current = setInterval(fetchBatchStatus, 5000);
            }
            fetchBatchStatus();
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error('Error retrying report:', error);
            }
        }
    };

    const handleViewReport = (reportId) => {
        navigate(`/admin/reports/${reportId}`);
    };

    const handleBackToList = () => {
        navigate('/admin/reports');
    };

    const getStatusChip = (status) => {
        const config = {
            pending: {
                icon: <HourglassEmptyIcon fontSize="small" />,
                color: 'default',
                label: 'Pending'
            },
            processing: {
                icon: <SyncIcon fontSize="small" sx={{
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                    }
                }} />,
                color: 'info',
                label: 'Processing'
            },
            completed: {
                icon: <CheckCircleIcon fontSize="small" />,
                color: 'success',
                label: 'Completed'
            },
            failed: {
                icon: <ErrorIcon fontSize="small" />,
                color: 'error',
                label: 'Failed'
            },
            draft: {
                icon: <CheckCircleIcon fontSize="small" />,
                color: 'warning',
                label: 'Draft'
            }
        };

        const { icon, color, label } = config[status] || config.pending;
        return (
            <Chip
                icon={icon}
                label={label}
                color={color}
                size="small"
                sx={{ minWidth: 100 }}
            />
        );
    };

    if (loading) {
        return <Loading />;
    }

    if (error) {
        return (
            <Box m="20px" marginX={3} marginY={3}>
                <Header title="Report Generation Progress" subtitle="Track your report generation" />
                <Alert severity="error">{error}</Alert>
                <Button onClick={handleBackToList} sx={{ mt: 2 }}>
                    Back to Reports
                </Button>
            </Box>
        );
    }

    if (!batchData) {
        return <Loading />;
    }

    const progress = batchData.total > 0
        ? Math.round(
            batchData.reports.reduce((sum, report) => {
                if (report.status === 'completed' || report.status === 'failed') {
                    return sum + 100;
                }
                return sum + (report.progress || 0);
            }, 0) / batchData.total
        )
        : 0;

    const isComplete = batchData.pending + batchData.processing === 0;

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Header
                title="Report Generation Progress"
                subtitle={`Batch ID: ${batchId.substring(0, 8)}...`}
            />

            {/* Progress Summary */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h5">
                            Progress: {batchData.completed + batchData.failed} / {batchData.total}
                        </Typography>
                        <Typography variant="h6" color={isComplete ? 'success.main' : 'text.secondary'}>
                            {progress}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 10, borderRadius: 5 }}
                    />
                </Box>

                <Box display="flex" gap={2} mt={2}>
                    <Chip
                        label={`${batchData.completed} Completed`}
                        color="success"
                        variant="outlined"
                    />
                    <Chip
                        label={`${batchData.processing} Processing`}
                        color="info"
                        variant="outlined"
                    />
                    <Chip
                        label={`${batchData.pending} Pending`}
                        color="default"
                        variant="outlined"
                    />
                    {batchData.failed > 0 && (
                        <Chip
                            label={`${batchData.failed} Failed`}
                            color="error"
                            variant="outlined"
                        />
                    )}
                </Box>

                {isComplete && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        All reports have been processed!
                    </Alert>
                )}
            </Paper>

            {/* Reports Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{
                            backgroundColor: theme.palette.mode === 'dark' ? colors.primary[300] : colors.grey[900],
                        }}>
                            <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'inherit' : colors.primary[100] }}><strong>Student</strong></TableCell>
                            <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'inherit' : colors.primary[100] }}><strong>Status</strong></TableCell>
                            <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'inherit' : colors.primary[100] }}><strong>Progress</strong></TableCell>
                            <TableCell sx={{ color: theme.palette.mode === 'dark' ? 'inherit' : colors.primary[100] }}><strong>Error</strong></TableCell>
                            <TableCell align="right" sx={{ color: theme.palette.mode === 'dark' ? 'inherit' : colors.primary[100] }}><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {batchData.reports && batchData.reports.map((report) => (
                            <TableRow
                                key={report.id}
                                sx={{
                                    backgroundColor: colors.primary[750],
                                    '&:hover': { backgroundColor: colors.primary[700] }
                                }}
                            >
                                <TableCell>{report.student_name || 'Unknown'}</TableCell>
                                <TableCell>{getStatusChip(report.status)}</TableCell>
                                <TableCell>
                                    {(report.status === 'processing' || report.status === 'pending') && report.progress && (
                                        <Typography variant="caption">
                                            {report.progress}%
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {report.error_message && (
                                        <Typography variant="caption" color="error">
                                            {report.error_message}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {report.status === 'completed' && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => handleViewReport(report.uuid)}
                                        >
                                            View Report
                                        </Button>
                                    )}
                                    {report.status === 'failed' && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            onClick={() => handleRetry(report.uuid)}
                                        >
                                            Retry
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Actions */}
            <Box mt={3} display="flex" gap={2}>
                <Button
                    variant="outlined"
                    onClick={handleBackToList}
                >
                    Back to Reports
                </Button>
                {isComplete && (
                    <Button
                        variant="contained"
                        onClick={() => navigate('/admin/reports/generate')}
                    >
                        Generate More Reports
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default ReportProgress;
